import type { Dimension, ExportFormat, Lod, MeshData } from "../types.ts";
import type { BuildStats, DesignDescription, ValidationIssue, WorkerRequest, WorkerResponse } from "./protocol.ts";

export interface BuildResult {
  mesh: MeshData;
  stats: BuildStats;
  issues: ValidationIssue[];
  /** Dimension lines from the design's `dimensions()`; empty when it has none. */
  dimensions: Dimension[];
}

export interface ExportResult {
  data: ArrayBuffer;
  format: ExportFormat;
  filename: string;
}

type Pending = { resolve: (v: WorkerResponse) => void; reject: (e: Error) => void };
type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;
type RequestBody = DistributiveOmit<WorkerRequest, "jobId">;

/**
 * Thin promise wrapper over one worker. Requests are processed in order by the
 * worker; `latestOnly` channels drop stale results on the client side and
 * collapse bursts so at most one build is queued behind the one in flight.
 */
export class WorkerChannel {
  private nextJob = 1;
  private pending = new Map<number, Pending>();
  private readyPromise: Promise<void>;
  private loaded = new Set<string>();
  private loading = new Map<string, Promise<DesignDescription>>();

  constructor(readonly worker: Worker) {
    this.readyPromise = new Promise<void>((resolve, reject) => {
      const onReady = (ev: MessageEvent<WorkerResponse>) => {
        if (ev.data.type === "ready") {
          worker.removeEventListener("message", onReady);
          resolve();
        } else if (ev.data.type === "error" && ev.data.jobId === -1) {
          reject(new Error(ev.data.message));
        }
      };
      worker.addEventListener("message", onReady);
    });
    worker.addEventListener("message", (ev: MessageEvent<WorkerResponse>) => {
      const msg = ev.data;
      if (!("jobId" in msg)) return;
      const p = this.pending.get(msg.jobId);
      if (!p) return;
      this.pending.delete(msg.jobId);
      if (msg.type === "error") p.reject(Object.assign(new Error(msg.message), { workerStack: msg.stack }));
      else p.resolve(msg);
    });
    worker.addEventListener("error", (ev) => {
      const err = new Error(ev.message ?? "Worker crashed");
      for (const p of this.pending.values()) p.reject(err);
      this.pending.clear();
    });
  }

  ready(): Promise<void> {
    return this.readyPromise;
  }

  private send(req: RequestBody): Promise<WorkerResponse> {
    const jobId = this.nextJob++;
    return new Promise((resolve, reject) => {
      this.pending.set(jobId, { resolve, reject });
      this.worker.postMessage({ ...req, jobId });
    });
  }

  async load(key: string, url: string): Promise<DesignDescription> {
    await this.ready();
    const inflight = this.loading.get(key);
    if (inflight) return inflight;
    const p = this.send({ type: "load", key, url }).then((r) => {
      if (r.type !== "loaded") throw new Error("unexpected response");
      this.loaded.add(key);
      this.loading.delete(key);
      return r.description;
    });
    this.loading.set(key, p);
    return p;
  }

  isLoaded(key: string): boolean {
    return this.loaded.has(key);
  }

  async describe(key: string): Promise<DesignDescription> {
    await this.ready();
    const r = await this.send({ type: "describe", key });
    if (r.type !== "described") throw new Error("unexpected response");
    this.loaded.add(key);
    return r.description;
  }

  async build(key: string, params: Record<string, unknown>, lod: Lod): Promise<BuildResult> {
    await this.ready();
    const r = await this.send({ type: "build", key, params, lod });
    if (r.type !== "built") throw new Error("unexpected response");
    return { mesh: r.mesh, stats: r.stats, issues: r.issues, dimensions: r.dimensions ?? [] };
  }

  async export(key: string, params: Record<string, unknown>, format: ExportFormat): Promise<ExportResult> {
    await this.ready();
    const r = await this.send({ type: "export", key, params, format });
    if (r.type !== "exported") throw new Error("unexpected response");
    return { data: r.data, format: r.format, filename: r.filename };
  }

  async migrate(key: string, fromVersion: string, params: Record<string, unknown>): Promise<Record<string, unknown>> {
    await this.ready();
    const r = await this.send({ type: "migrate", key, fromVersion, params });
    if (r.type !== "migrated") throw new Error("unexpected response");
    return r.params;
  }

  terminate(): void {
    this.worker.terminate();
    const err = new Error("Worker terminated");
    for (const p of this.pending.values()) p.reject(err);
    this.pending.clear();
  }
}

/**
 * Latest-wins build queue for the live preview: while a build is in flight,
 * newer requests replace each other and only the newest runs afterwards.
 */
export class PreviewQueue {
  private inFlight = false;
  private next: { key: string; params: Record<string, unknown>; lod: Lod; resolve: (r: BuildResult | null) => void } | null = null;

  constructor(private readonly channel: WorkerChannel) {}

  request(key: string, params: Record<string, unknown>, lod: Lod): Promise<BuildResult | null> {
    return new Promise((resolve) => {
      if (this.next) this.next.resolve(null); // superseded
      this.next = { key, params, lod, resolve };
      void this.pump();
    });
  }

  private async pump(): Promise<void> {
    if (this.inFlight || !this.next) return;
    const job = this.next;
    this.next = null;
    this.inFlight = true;
    try {
      const result = await this.channel.build(job.key, job.params, job.lod);
      job.resolve(result);
    } catch (err) {
      job.resolve(null);
      this.onError?.(err as Error);
    } finally {
      this.inFlight = false;
      void this.pump();
    }
  }

  onError?: (err: Error) => void;
}

/**
 * Two workers per configurator: one for the live preview, one for exports and
 * migrations, so a heavy export never stalls the viewport.
 */
export class KernelClient {
  readonly preview: WorkerChannel;
  readonly heavy: WorkerChannel;
  readonly previewQueue: PreviewQueue;

  constructor(createWorker: () => Worker) {
    this.preview = new WorkerChannel(createWorker());
    this.heavy = new WorkerChannel(createWorker());
    this.previewQueue = new PreviewQueue(this.preview);
  }

  /** Load a design bundle into both workers. */
  async load(key: string, url: string): Promise<DesignDescription> {
    const [desc] = await Promise.all([this.preview.load(key, url), this.heavy.load(key, url)]);
    return desc;
  }

  async describe(key: string): Promise<DesignDescription> {
    return this.preview.describe(key);
  }

  buildPreview(key: string, params: Record<string, unknown>): Promise<BuildResult | null> {
    return this.previewQueue.request(key, params, "preview");
  }

  buildExportQuality(key: string, params: Record<string, unknown>): Promise<BuildResult> {
    return this.heavy.build(key, params, "export");
  }

  export(key: string, params: Record<string, unknown>, format: ExportFormat): Promise<ExportResult> {
    return this.heavy.export(key, params, format);
  }

  migrate(key: string, fromVersion: string, params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.heavy.migrate(key, fromVersion, params);
  }

  terminate(): void {
    this.preview.terminate();
    this.heavy.terminate();
  }
}
